import React, { useState, useEffect } from "react";
import { Select, Drawer } from "antd";
import Navbar from "../../components/Navbar/Navbar";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import i18next from "i18next";
import {
  getBillPaymentHistory,
  getPurchaseList,
  getVendors,
} from "../../API  Functions/PurchaseFunctions";
import moment from "moment";
import PurchasePayment from "../../modals/purchasePaymentModal";
import SearchBox from "../../components/CustomWidgets/SearchBox";
import SelectBox from "../../components/CustomWidgets/EOrderFilter";
import VendorPaymentTable from "./VendorPaymentTable";
import ShimmerLoader from "../../loading";
import DateFilter from "../../components/ui/DateFilter";
import dayjs from "dayjs";
import { checkPermission } from "../../Utils/permissionHelper";

const { Option } = Select;

const today = dayjs();
const formatToday = today.format("YYYY-MM-DD");
const startofMonth = today.startOf("month").format("YYYY-MM-DD");

/**
 * Flattens getPurchaseList's nested structure into the flat shape
 * VendorPaymentTable expects, while keeping _raw for row-click navigation.
 */
const flattenRow = (val) => {
  const od = val?.order_details ?? {};
  return {
    // ── original vendor-payment fields ──────────────────────────────────────
    order_no:         od.order_no       ?? "",
    supplier_name:    od.partner_name   ?? "",
    supplier_id:      od.partner_id     ?? null,
    order_date:       od.order_date     ?? "",
    vendor_reference: od.venfor_ref     ?? "",
    invoice_value:    od.total_amount   ?? 0,
    paid_amount:      od.paid_amount    ?? 0,
    due_amount:       od.due_amount     ?? 0,
    store_name:       od.store_name     ?? "",
    bill_id:          od.bill_id        ?? null,
    // ── new fields from purchase history ────────────────────────────────────
    status:           od.status         ?? "",
    payment_status:   od.payment_status ?? "",
    create_date:      od.create_date    ?? "",
    due_date:         od.due_date       ?? "",
    payment_term:     od.payment_term   ?? "",
    quantity:         val.lines?.reduce((s, l) => s + (l.product_qty ?? 0), 0) ?? 0,
    // ── raw original kept so row-click can pass full orderData ───────────────
    _raw: val,
  };
};

const SupplyPayment = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const user_id    = useSelector((state) => state?.login?.user?.result?.user_id);
  const isGinne    = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);

  const fromPage      = location.state?.from;
  const orderIdToOpen = location.state?.orderId;

  // ── state ──────────────────────────────────────────────────────────────────
  const [popup,              setPopup]              = useState(false);
  const [selectedBillId,     setSelectedBillId]     = useState(null);
  const [selectedDueAmount,  setSelectedDueAmount]  = useState(null);
  const [visible,            setVisible]            = useState(false);
  const [paymentHistoryData, setPaymentHistoryData] = useState([]);
  const [loading,            setLoading]            = useState(false);
  const [isSearching,        setIsSearching]        = useState(false);
  const [isModalVisible,     setIsModalVisible]     = useState(false);
  const [searchKeyword,      setSearchKeyword]      = useState("");
  const [selectedPaymentType,setSelectedPaymentType]= useState(0);
  const [data,               setData]               = useState([]);
  const [vendors,            setVendors]            = useState([]);
  const [vendorLoader,       setVendorLoading]      = useState(false);
  const [selectedVendor,     setSelectedVendor]     = useState("");
  const [selectedVendorID,   setSelectedVendorID]   = useState("");
  const [startDate,          setStartDate]          = useState(startofMonth);
  const [endDate,            setEndDate]            = useState(formatToday);
  const [selectedRange,      setSelectedRange]      = useState("thisMonth");
  const [currentLimit,       setCurrentLimit]       = useState(10);
  const [currentOffset,      setCurrentOffset]      = useState(0);

  // ── filter options (matching PurchaseList) ─────────────────────────────────
  const filterOptions = [
    { value: 0, label: t("all_orders") },
    { value: 1, label: t("Draft Orders") },
    { value: 2, label: t("Purchase Orders") },
  ];

  const toStateParam = (n) => {
    if (Number(n) === 1) return "draft";
    if (Number(n) === 2) return "done";
    return "";
  };

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchData = (
    search      = "",
    paymentType = 0,
    offset      = 0,
    start       = startDate,
    end         = endDate,
    vendorID    = selectedVendorID || null
  ) => {
    if (search) setIsSearching(true);
    else        setLoading(true);

    const payload = {
      lang:       i18next.language === "ar" ? "ar_001" : "en_US",
      user_id,
      state:      toStateParam(paymentType),
      order_no:   search,
      limit:      currentLimit,
      offset,
      start_date: start || "",
      end_date:   end   || "",
      ...(vendorID && { vendor_id: vendorID }),
    };

    getPurchaseList(payload)
      .then((res) => {
        if (res?.result?.[0]?.status_code === 200) {
          setData(res.result[0].response.map(flattenRow));
        } else {
          setData([]);
        }
        setIsSearching(false);
        setLoading(false)
      })
      .catch((err) => {
        console.error("Error fetching purchase list:", err);
        setIsSearching(false);
        setLoading(false);
      });
  };

  // ── payment history drawer ─────────────────────────────────────────────────
  const showDrawer = (row) => {
    setVisible(true);
    getBillPaymentHistory({ bill_id: row.bill_id })
      .then((res) => {
        setPaymentHistoryData(
          res?.result?.payments?.length > 0 ? res.result.payments : []
        );
      })
      .catch((err) => console.error(err));
  };

  const onClose = () => setVisible(false);

  // ── vendors ────────────────────────────────────────────────────────────────
  const getVendorsData = () => {
    setVendorLoading(true);
    getVendors(user_id).then((res) => {
      setVendors(res);
      setVendorLoading(false);
    });
  };

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    getVendorsData();
    fetchData("", 0, 0, startDate, endDate);
  }, [popup]);

  useEffect(() => {
    fetchData("", 0, 0, startDate, endDate);
  }, []);

  useEffect(() => {
    fetchData(
      searchKeyword,
      selectedPaymentType,
      currentOffset,
      startDate,
      endDate,
      selectedVendorID || null
    );
  }, [currentLimit, currentOffset]);

  useEffect(() => {
    if (fromPage === "vendor_payment" && orderIdToOpen) {
      fetchData(orderIdToOpen, 0, 0, startofMonth, formatToday);
    }
  }, [fromPage, orderIdToOpen]);

  // ── handlers ───────────────────────────────────────────────────────────────
  const handlePaymentClick = (row) => {
    if (!checkPermission("/purchase/supplyPayment", isGinne)) return;
    setSelectedBillId(row.bill_id);
    setSelectedDueAmount(row.due_amount);
    setIsModalVisible(true);
  };

  const handleSearchChange = (value) => {
    setSearchKeyword(value);
    fetchData(value, selectedPaymentType, 0, startDate, endDate, selectedVendorID || null);
  };

  const handlePaymentTypeChange = (event) => {
    const paymentType = Number(event.target.value);
    setSelectedPaymentType(paymentType);
    fetchData(searchKeyword, paymentType, 0, startDate, endDate, selectedVendorID || null);
  };

  const handleSelectVendor = (val) => {
    if (val === "All Vendors") {
      setSelectedVendorID(null);
      setSelectedVendor("");
      fetchData(searchKeyword, selectedPaymentType, 0, startDate, endDate, null);
      return;
    }
    setSelectedVendorID(val);
    setSelectedVendor(vendors?.find((v) => v.id === val)?.name ?? "");
    fetchData(searchKeyword, selectedPaymentType, 0, startDate, endDate, val);
  };

  const handleDateFilter = (start, end) => {
    setSearchKeyword("");
    setStartDate(start);
    setEndDate(end);
    setCurrentLimit(10);
    setCurrentOffset(0);
    fetchData("", selectedPaymentType, 0, start, end, selectedVendorID || null);
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="w-full flex flex-col bg-[#F6F7F9] min-h-[93vh]">
        <Navbar heading="Vendor Invoices" />

        <div className="px-4 font-extrabold overflow-y-auto overflow-hidden custom-scrollbar">

          {/* Optimized Top Bar Structure */}
          <div className="flex flex-row justify-between items-center mt-4 mb-4 gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px] max-w-md relative top-1">
              <SearchBox
                placeholder={t("Search Vendor/Order No")}
                searchResult={handleSearchChange}
                loading={isSearching}
              />
            </div>

            <div className="flex flex-row items-center gap-3 flex-wrap ">
              <Select
                className="overflow-hidden relative top-1"
                style={{ width: 200, minWidth: 200, height: 40 }}
                placeholder={
                  vendorLoader ? (
                    <span className="flex items-center gap-2 text-gray-400">
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
                      {t("Loading vendors...")}
                    </span>
                  ) : t("Select Vendor")
                }
                value={selectedVendor || "All Vendors"}
                onChange={handleSelectVendor}
                disabled={vendorLoader}
              >
                <Option value="All Vendors">{t("All Vendors")}</Option>
                {vendors?.map(({ id, name, phone, email }) => {
                  let label = name;
                  if (phone)      label += ` (${phone})`;
                  else if (email) label += ` (${email})`;
                  return <Option key={id} value={id}>{label}</Option>;
                })}
              </Select>

              {/* Stabilized width to prevent layout skewing */}
              <div style={{ width: 160, minWidth: 160 }}>
                <SelectBox
                  style={{ width: "100%", height: 40 }}
                  width={160}
                  placeholder={t("all_orders")}
                  name="filter"
                  onChange={handlePaymentTypeChange}
                  options={filterOptions}
                  value={selectedPaymentType}
                />
              </div>

              <DateFilter
                setSelectedRange={setSelectedRange}
                selectedRange={selectedRange}
                SubmitFilter={handleDateFilter}
              />
            </div>
          </div>

          {/* table */}
          {loading ? (
            <ShimmerLoader />
          ) : (
            <div className="overflow-x-auto w-full">
              <VendorPaymentTable
                handlePaymentClick={handlePaymentClick}
                showDrawer={showDrawer}
                additionalData={data}
                pagination={(values) => {
                  setCurrentLimit(values.limit);
                  setCurrentOffset(values.offset);
                }}
                showPaymenthistory={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* payment modal */}
      {isModalVisible && (
        <PurchasePayment
          isOpen={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          bill_id={selectedBillId}
          page="supplyPayment"
          due_amount={selectedDueAmount}
          Popup={() => setPopup(true)}
        />
      )}

      {/* payment history drawer */}
      <Drawer
        title={t("Payment History")}
        placement="right"
        width={500}
        onClose={onClose}
        visible={visible}
      >
        {paymentHistoryData.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2 text-start">{t("Payment Date & Time")}</th>
                <th className="p-2 text-start">{t("Type")}</th>
                <th className="p-2 text-start">{t("amount")}</th>
              </tr>
            </thead>
            <tbody>
              {paymentHistoryData.map((payment, index) => (
                <tr key={index} className="border-b">
                  <td className="p-2">
                    {moment(payment.payment_date).format("YYYY-MM-DD HH:mm:ss")}
                  </td>
                  <td className="p-2">{t(payment.type)}</td>
                  <td className="p-2">EGP {payment.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{t("No payment history available")}</p>
        )}
      </Drawer>
    </>
  );
};

export default SupplyPayment;